import { useCallback, useState } from "react"
import { toast } from "sonner"
import type { ActionPayload, RefreshEnvelope } from "./types"

// Hook bound to one (ticketId, companyAppId) pair. Each invocation
// POSTs the partner action through Valet's portal proxy at
// `/tickets/:ticketId/company_apps/:companyAppId/action`, returning
// the refresh envelope so the caller can decide whether to swap blocks
// in place, refetch, or do nothing.
//
// Server-side: `Ui::Documents::RunAction` re-validates the URL +
// method + role, signs + calls the partner via `Companies::Client`,
// writes an Activity row, busts the cached document, and validates
// each `partial` refresh block before returning. The hook just relays
// the response.
//
// Errors surface as a sonner toast and return `null` so the renderer
// keeps the existing tree untouched.
type Result = {
  runAction: (action: ActionPayload, refresh?: string[]) => Promise<RefreshEnvelope | null>
  isLoading: boolean
  error: string | null
}

export function useAction(ticketId: string, companyAppId: number): Result {
  const [isLoading, setIsLoading] = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  const runAction = useCallback(
    async (action: ActionPayload, refresh?: string[]) => {
      setIsLoading(true)
      setError(null)
      const csrf =
        typeof document !== "undefined"
          ? document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
          : ""
      try {
        const res = await fetch(
          `/tickets/${ticketId}/company_apps/${companyAppId}/action`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              "Accept":       "application/json",
              "X-CSRF-Token": csrf,
            },
            body: JSON.stringify({
              method:    action.method,
              path:      action.path,
              body:      action.body ?? {},
              action_id: action.action_id ?? null,
              block_ids: refresh ?? action.refresh ?? [],
            }),
          },
        )
        const json = await res.json().catch(() => ({}))
        if (!res.ok || json?.ok === false) {
          const msg = json?.error || `Action failed (${res.status}).`
          setError(msg)
          toast.error(`${action.label}: ${msg}`)
          return null
        }
        toast.success(`${action.label} succeeded.`)
        // Normalize the refresh envelope shape — the server returns
        // string `scope`s (`"full" | "partial" | "none"`), the
        // renderer types match.
        const refreshEnvelope: RefreshEnvelope = json.refresh ?? { scope: "full" }
        return refreshEnvelope
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Action failed."
        setError(msg)
        toast.error(`${action.label}: ${msg}`)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [ticketId, companyAppId],
  )

  return { runAction, isLoading, error }
}
